import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import {
  Users, CalendarDays, FileSpreadsheet, DollarSign, ChevronDown, ChevronUp,
  Download, Save, Mail, Loader2, IndianRupee, Upload, FileWarning, X, History,
  RefreshCw, RotateCw, Copy, Send,
} from 'lucide-react';
import MultiSelectCombobox from './MultiSelectCombobox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i);

const formatMoney = (v, currency = 'INR') => {
  const num = Number(v);
  if (Number.isNaN(num)) return '—';
  const symbol = currency === 'INR' ? '₹' : `${currency} `;
  return `${symbol}${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const daysInMonth = (year, monthIdx1) => new Date(year, monthIdx1, 0).getDate();

const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const PayDistributionTab = () => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [components, setComponents] = useState([]);
  const [activeTab, setActiveTab] = useState('manual');

  // Selections
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(currentYear);
  const [cycleFrom, setCycleFrom] = useState('');
  const [cycleTo, setCycleTo] = useState('');

  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, components, summary, ... }
  const [expandedRow, setExpandedRow] = useState(null);

  const [savingRun, setSavingRun] = useState(false);
  const [savePrompt, setSavePrompt] = useState(false);
  const [sendEmailsOnSave, setSendEmailsOnSave] = useState(false);

  // When set, the next Finalize call marks this previous run as
  // ``superseded`` — powers the "Reopen into Manual" re-run action.
  const [supersedesRunId, setSupersedesRunId] = useState(null);
  const [supersedesRunLabel, setSupersedesRunLabel] = useState('');
  const [historyRefreshTick, setHistoryRefreshTick] = useState(0);

  const token = localStorage.getItem('token');
  const config = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // ---- initial data load ------------------------------------------------
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, compRes] = await Promise.all([
        axios.get(`${API}/payroll/pay-distribution/active-employees`, config),
        axios.get(`${API}/payroll/components`, config),
      ]);
      setEmployees(empRes.data || []);
      setComponents(compRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load pay-distribution data');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => { loadData(); }, [loadData]);

  // ---- auto-fill cycle dates whenever month/year changes ---------------
  useEffect(() => {
    const last = daysInMonth(year, month);
    setCycleFrom(toIso(new Date(year, month - 1, 1)));
    setCycleTo(toIso(new Date(year, month - 1, last)));
  }, [month, year]);

  // ---- picker options --------------------------------------------------
  const employeeOptions = useMemo(() => employees.map((e) => ({
    value: e.id,
    label: `${e.employee_id} · ${e.name}`,
    sublabel: `${e.department || 'No dept'}${e.email ? ' · ' + e.email : ''}${e.monthly_base ? ' · ' + formatMoney(e.monthly_base) + '/mo' : ''}`,
  })), [employees]);

  const componentOptions = useMemo(() => components.map((c) => ({
    value: c.id,
    label: c.name,
    sublabel: `${c.component_type}${c.component_type === 'variable' && c.percentage != null ? ` · ${c.percentage}%` : ''}${c.component_type === 'fixed' && c.amount != null ? ` · ${formatMoney(c.amount)}` : ''} · ${(c.frequency || 'monthly').replace('_', ' ')}${c.is_deduction ? ' · deduction' : ''}`,
  })), [components]);

  // ---- generate --------------------------------------------------------
  const canGenerate = selectedEmployees.length > 0
    && selectedComponents.length > 0
    && cycleFrom && cycleTo
    && cycleFrom <= cycleTo;

  const handleGenerate = async () => {
    if (!canGenerate) {
      toast.error('Please fill all selections');
      return;
    }
    setGenerating(true);
    setPreview(null);
    setExpandedRow(null);
    try {
      const res = await axios.post(`${API}/payroll/pay-distribution/generate`, {
        employee_ids: selectedEmployees,
        component_ids: selectedComponents,
        month, year,
        cycle_from: cycleFrom,
        cycle_to: cycleTo,
      }, config);
      setPreview(res.data);
      toast.success(`Preview generated for ${res.data.rows.length} employee(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate distribution');
    } finally {
      setGenerating(false);
    }
  };

  // ---- save + optional email ------------------------------------------
  const handleFinalize = async () => {
    setSavingRun(true);
    try {
      const res = await axios.post(`${API}/payroll/pay-distribution/finalize`, {
        employee_ids: selectedEmployees,
        component_ids: selectedComponents,
        month, year,
        cycle_from: cycleFrom,
        cycle_to: cycleTo,
        send_emails: sendEmailsOnSave,
        supersedes_run_id: supersedesRunId || null,
      }, config);
      toast.success(res.data.message || 'Run saved');
      setSavePrompt(false);
      setSupersedesRunId(null);
      setSupersedesRunLabel('');
      setHistoryRefreshTick((t) => t + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save run');
    } finally {
      setSavingRun(false);
    }
  };

  // ---- Re-run: reopen a past run into the Manual form -----------------
  const handleReopenIntoManual = useCallback((run) => {
    if (!run) return;
    // Prefill every input the Manual flow needs. Employee + component IDs
    // are stored on the run doc itself; ``useEffect`` won't clobber our
    // cycle_from/cycle_to because it only fires when month/year change,
    // so we set month/year FIRST and then override the cycle dates.
    setMonth(run.month || new Date().getMonth() + 1);
    setYear(run.year || currentYear);
    // Defer the cycle-window override to the next tick so the month/year
    // effect has already run and can't overwrite it.
    setTimeout(() => {
      if (run.cycle_from) setCycleFrom(run.cycle_from);
      if (run.cycle_to) setCycleTo(run.cycle_to);
    }, 0);
    setSelectedEmployees(run.employee_ids || []);
    setSelectedComponents(run.component_ids || []);
    setPreview(null);
    setExpandedRow(null);
    setSupersedesRunId(run.id);
    setSupersedesRunLabel(run.month_label || `${run.year}-${run.month}`);
    setActiveTab('manual');
    toast.success(`Loaded ${run.month_label || 'run'} into Manual — click Generate to preview.`);
  }, []);

  const clearSupersede = () => {
    setSupersedesRunId(null);
    setSupersedesRunLabel('');
  };

  // ---- CSV export -----------------------------------------------------
  const handleExportCsv = () => {
    if (!preview) return;
    const compNames = preview.components.map((c) => c.name);
    const header = ['Employee ID', 'Name', 'Email', 'Department', 'Currency',
      'Monthly Base', ...compNames, 'Additions', 'Deductions', 'Net Pay'];
    const rows = preview.rows.map((r) => {
      const byId = Object.fromEntries(r.components.map((c) => [c.id, c.cycle_amount]));
      const compCols = preview.components.map((c) => byId[c.id] ?? 0);
      return [
        r.employee_id, r.name, r.email, r.department, r.salary_currency,
        r.monthly_base, ...compCols,
        r.totals.additions, r.totals.deductions, r.totals.net_pay,
      ];
    });
    const csv = [header, ...rows]
      .map((line) => line.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pay-distribution-${preview.year}-${String(preview.month).padStart(2, '0')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ---- rendering helpers ----------------------------------------------
  const componentColMap = useMemo(() => {
    if (!preview) return {};
    const m = {};
    preview.components.forEach((c) => { m[c.id] = c; });
    return m;
  }, [preview]);

  const summarySelectAllEmployees = () => setSelectedEmployees(employees.map((e) => e.id));
  const summarySelectAllComponents = () => setSelectedComponents(components.map((c) => c.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="pay-distribution-loading">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 min-w-0" data-testid="pay-distribution-tab">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 lg:w-[560px]">
          <TabsTrigger value="manual" data-testid="pd-mode-manual">
            <DollarSign className="w-4 h-4 mr-2" />
            Manual Selection
          </TabsTrigger>
          <TabsTrigger value="bulk" data-testid="pd-mode-bulk">
            <Upload className="w-4 h-4 mr-2" />
            Bulk Upload (Excel)
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="pd-mode-history">
            <History className="w-4 h-4 mr-2" />
            Run History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manual" className="space-y-6">
      {supersedesRunId && (
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 border border-amber-200 bg-amber-50 rounded-lg text-sm text-amber-900"
          data-testid="supersedes-banner"
        >
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-700" />
            <span>
              Re-running <strong>{supersedesRunLabel}</strong> — saving will supersede the previous run and mark it as replaced in Run History.
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSupersede}
            className="h-7 text-amber-800 hover:bg-amber-100"
            data-testid="clear-supersede-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" /> Cancel re-run
          </Button>
        </div>
      )}
      {/* Input Selectors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            Pay Distribution — Inputs
          </CardTitle>
          <CardDescription>
            Pick employees, month/year, cycle window and salary components to build a distribution.
            Basic salary and monthly performance pay are prorated by cycle days; all other
            components stay at their full monthly value.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1. Employees */}
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Employees ({employees.length} active)</Label>
              <button
                type="button"
                onClick={summarySelectAllEmployees}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="employees-quick-select-all"
              >
                Select all active
              </button>
            </div>
            <MultiSelectCombobox
              options={employeeOptions}
              selected={selectedEmployees}
              onChange={setSelectedEmployees}
              placeholder="Select employees…"
              searchPlaceholder="Search by name, ID, email, department…"
              triggerTestId="employees-picker"
            />
          </div>

          {/* 2. Month / Year */}
          <div className="space-y-2">
            <Label>Payroll Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
              <SelectTrigger data-testid="month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Payroll Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
              <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* 3. Cycle From / To */}
          <div className="space-y-2">
            <Label>Cycle From</Label>
            <Input
              type="date"
              value={cycleFrom}
              onChange={(e) => setCycleFrom(e.target.value)}
              data-testid="cycle-from-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Cycle To</Label>
            <Input
              type="date"
              value={cycleTo}
              onChange={(e) => setCycleTo(e.target.value)}
              data-testid="cycle-to-input"
            />
          </div>

          {/* 4. Salary Components */}
          <div className="space-y-2 md:col-span-2">
            <div className="flex items-center justify-between">
              <Label>Salary Components ({components.length} available)</Label>
              <button
                type="button"
                onClick={summarySelectAllComponents}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                data-testid="components-quick-select-all"
              >
                Select all components
              </button>
            </div>
            <MultiSelectCombobox
              options={componentOptions}
              selected={selectedComponents}
              onChange={setSelectedComponents}
              placeholder="Select salary components…"
              searchPlaceholder="Search components…"
              triggerTestId="components-picker"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-3 pt-2">
            <Button
              onClick={handleGenerate}
              disabled={!canGenerate || generating}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="generate-distribution-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Generate Distribution
            </Button>
            {preview && (
              <>
                <Button
                  variant="outline"
                  onClick={handleExportCsv}
                  data-testid="export-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={() => setSavePrompt(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="save-run-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Run
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {preview && (
        <PreviewSection
          preview={preview}
          componentColMap={componentColMap}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
        />
      )}

      {/* Save confirmation */}
      <AlertDialog open={savePrompt} onOpenChange={setSavePrompt}>
        <AlertDialogContent data-testid="save-run-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save Pay Distribution Run?</AlertDialogTitle>
            <AlertDialogDescription>
              This will persist the run for {preview?.rows?.length ?? 0} employee(s) and add each
              payslip to the employee&apos;s Payslip portal. Choose whether to email the payslips as
              well — emails go to each employee&apos;s registered address.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-700 select-none px-1">
            <input
              type="checkbox"
              checked={sendEmailsOnSave}
              onChange={(e) => setSendEmailsOnSave(e.target.checked)}
              data-testid="send-emails-checkbox"
              className="w-4 h-4"
            />
            <Mail className="w-4 h-4 text-slate-500" />
            Also email the payslip to each employee
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="save-run-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalize}
              disabled={savingRun}
              data-testid="save-run-confirm"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingRun ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {sendEmailsOnSave ? 'Save & Email' : 'Save Only'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </TabsContent>

        <TabsContent value="bulk" className="space-y-6">
          <BulkUploadSection
            onFinalized={() => { /* future: refresh a run-history panel */ }}
          />
        </TabsContent>

        <TabsContent value="history" className="space-y-6">
          <RunHistorySection
            onReopenIntoManual={handleReopenIntoManual}
            refreshTick={historyRefreshTick}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ---- preview section (matrix + expandable cards) ------------------------

const PreviewSection = ({ preview, componentColMap, expandedRow, setExpandedRow }) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
            Distribution Matrix — {preview.month_label}
          </CardTitle>
          <CardDescription>
            Cycle {preview.cycle_from} → {preview.cycle_to} · {preview.days_in_cycle} of {preview.days_in_month} days
            · proration factor <span className="font-medium">{preview.proration_factor.toFixed(4)}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table data-testid="distribution-matrix">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[180px]">Employee</TableHead>
                {preview.components.map((c) => (
                  <TableHead key={c.id} className="text-right whitespace-nowrap">
                    {c.name}
                    {c.is_deduction && <span className="ml-1 text-[10px] text-red-500">(−)</span>}
                  </TableHead>
                ))}
                <TableHead className="text-right whitespace-nowrap">Net Pay</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.rows.map((r) => {
                const cellsById = Object.fromEntries(r.components.map((c) => [c.id, c]));
                return (
                  <TableRow key={r.user_id} data-testid={`matrix-row-${r.employee_id}`}>
                    <TableCell>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {r.name}
                        {r.email_status && <DeliveryBadge status={r.email_status} />}
                      </div>
                      <div className="text-xs text-slate-500">{r.employee_id} · {r.department}</div>
                    </TableCell>
                    {preview.components.map((c) => {
                      const cell = cellsById[c.id];
                      const val = cell?.cycle_amount ?? 0;
                      return (
                        <TableCell key={c.id} className="text-right whitespace-nowrap tabular-nums">
                          <span className={c.is_deduction ? 'text-red-600' : 'text-slate-700'}>
                            {c.is_deduction ? '−' : ''}{formatMoney(val, r.salary_currency)}
                          </span>
                          {cell?.prorated && (
                            <span className="ml-1 text-[9px] uppercase tracking-wide text-indigo-500">prorated</span>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right font-semibold whitespace-nowrap tabular-nums text-emerald-700">
                      {formatMoney(r.totals.net_pay, r.salary_currency)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow className="bg-slate-50 font-semibold" data-testid="matrix-totals-row">
                <TableCell>Totals ({preview.summary.employee_count})</TableCell>
                {preview.components.map((c) => {
                  const sum = preview.rows.reduce((acc, r) => {
                    const cell = r.components.find((x) => x.id === c.id);
                    return acc + (cell?.cycle_amount ?? 0);
                  }, 0);
                  return (
                    <TableCell key={c.id} className="text-right whitespace-nowrap tabular-nums">
                      {c.is_deduction ? '−' : ''}{formatMoney(sum)}
                    </TableCell>
                  );
                })}
                <TableCell className="text-right whitespace-nowrap tabular-nums text-emerald-700">
                  {formatMoney(preview.summary.total_net_pay)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="w-5 h-5 text-indigo-600" />
            Per-Employee Payslip Preview
          </CardTitle>
          <CardDescription>Expand any employee to see the full payslip that will be sent/saved.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {preview.rows.map((r) => {
            const open = expandedRow === r.user_id;
            return (
              <div key={r.user_id} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedRow(open ? null : r.user_id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
                  data-testid={`payslip-card-${r.employee_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-700 font-semibold flex items-center justify-center text-sm">
                      {(r.name || 'E').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{r.name} <span className="text-slate-400">·</span> <span className="text-xs text-slate-500">{r.employee_id}</span></div>
                      <div className="text-xs text-slate-500 truncate">{r.designation} {r.department && '·'} {r.department}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 tabular-nums">
                      <IndianRupee className="w-3 h-3 mr-0.5" />{formatMoney(r.totals.net_pay, r.salary_currency).replace('₹', '')}
                    </Badge>
                    {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1" data-testid={`payslip-body-${r.employee_id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mb-3">
                      <InfoBlock label="Monthly Base" value={formatMoney(r.monthly_base, r.salary_currency)} />
                      <InfoBlock label="Variable Base" value={formatMoney(r.variable_base, r.salary_currency)} />
                      <InfoBlock label="Cycle" value={`${preview.cycle_from} → ${preview.cycle_to}`} />
                    </div>
                    <div className="overflow-x-auto rounded border border-slate-200">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Component</TableHead>
                            <TableHead>Type · Frequency</TableHead>
                            <TableHead className="text-right">Monthly</TableHead>
                            <TableHead className="text-right">Cycle</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {r.components.map((line) => (
                            <TableRow key={line.id}>
                              <TableCell>
                                {line.name}
                                {line.prorated && <span className="ml-2 text-[10px] uppercase tracking-wide text-indigo-500">prorated</span>}
                                {line.is_deduction && <Badge variant="destructive" className="ml-2">Deduction</Badge>}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">
                                {line.component_type} · {(line.frequency || 'monthly').replace('_', ' ')}
                              </TableCell>
                              <TableCell className="text-right tabular-nums">{formatMoney(line.monthly_amount, r.salary_currency)}</TableCell>
                              <TableCell className={`text-right font-medium tabular-nums ${line.is_deduction ? 'text-red-600' : 'text-slate-800'}`}>
                                {line.is_deduction ? '−' : ''}{formatMoney(line.cycle_amount, r.salary_currency)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-sm">
                      <div className="rounded bg-emerald-50 border border-emerald-100 p-2">
                        <div className="text-[11px] text-emerald-700 uppercase tracking-wide">Earnings</div>
                        <div className="font-semibold text-emerald-800 tabular-nums">{formatMoney(r.totals.additions, r.salary_currency)}</div>
                      </div>
                      <div className="rounded bg-red-50 border border-red-100 p-2">
                        <div className="text-[11px] text-red-700 uppercase tracking-wide">Deductions</div>
                        <div className="font-semibold text-red-800 tabular-nums">−{formatMoney(r.totals.deductions, r.salary_currency)}</div>
                      </div>
                      <div className="rounded bg-indigo-50 border border-indigo-100 p-2">
                        <div className="text-[11px] text-indigo-700 uppercase tracking-wide">Net Pay</div>
                        <div className="font-semibold text-indigo-800 tabular-nums">{formatMoney(r.totals.net_pay, r.salary_currency)}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </>
  );
};

const InfoBlock = ({ label, value }) => (
  <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className="text-sm font-medium text-slate-800">{value}</div>
  </div>
);

export default PayDistributionTab;

// ============= Bulk Upload Section =====================================

const BulkUploadSection = ({ onFinalized }) => {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingBulk, setSavingBulk] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { rows, errors, detected_components, ... }
  const [expandedRow, setExpandedRow] = useState(null);
  const [savePrompt, setSavePrompt] = useState(false);
  const [sendEmailsOnSave, setSendEmailsOnSave] = useState(false);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const config = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/payroll/pay-distribution/bulk-template`, {
        ...config,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pay-distribution-template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setUploadResult(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(
        `${API}/payroll/pay-distribution/bulk-upload`,
        form,
        { headers: { ...config.headers, 'Content-Type': 'multipart/form-data' } },
      );
      setUploadResult(res.data);
      if (res.data.valid_rows === 0) {
        toast.error(`No valid rows found — ${res.data.error_rows} error(s)`);
      } else {
        toast.success(`Parsed ${res.data.valid_rows} row(s) — ${res.data.error_rows} error(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      // Reset the input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClear = () => {
    setUploadResult(null);
    setFileName('');
    setExpandedRow(null);
  };

  const handleFinalizeBulk = async () => {
    if (!uploadResult || uploadResult.valid_rows === 0) return;
    setSavingBulk(true);
    try {
      const res = await axios.post(
        `${API}/payroll/pay-distribution/finalize-bulk`,
        { rows: uploadResult.rows, send_emails: sendEmailsOnSave },
        config,
      );
      toast.success(res.data.message || 'Saved');
      setSavePrompt(false);
      handleClear();
      if (onFinalized) onFinalized();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingBulk(false);
    }
  };

  // Build a matrix-friendly "preview" shape from the bulk result so we can
  // reuse ``PreviewSection`` verbatim.
  const bulkPreview = useMemo(() => {
    if (!uploadResult || uploadResult.valid_rows === 0) return null;
    // Union of all components used in the file
    const componentDict = new Map();
    uploadResult.rows.forEach((r) => {
      r.components.forEach((c) => componentDict.set(c.id, {
        id: c.id, name: c.name, component_type: c.component_type,
        is_deduction: c.is_deduction,
      }));
    });
    const components = Array.from(componentDict.values());

    // Header — use the earliest cycle_from / latest cycle_to from the file
    const cycleFrom = uploadResult.rows.reduce((a, r) => (!a || r.cycle_from < a ? r.cycle_from : a), null);
    const cycleTo = uploadResult.rows.reduce((a, r) => (!a || r.cycle_to > a ? r.cycle_to : a), null);
    const firstRow = uploadResult.rows[0];

    return {
      month: firstRow.month,
      year: firstRow.year,
      month_label: `${new Date(firstRow.year, firstRow.month - 1, 1).toLocaleString('default', { month: 'long' })} ${firstRow.year}`,
      cycle_from: cycleFrom,
      cycle_to: cycleTo,
      days_in_cycle: 0,
      days_in_month: 0,
      proration_factor: 1,
      components,
      rows: uploadResult.rows.map((r) => ({
        user_id: r.user_id,
        employee_id: r.employee_id,
        name: r.name,
        email: r.email,
        department: r.department,
        designation: r.designation,
        salary_currency: r.salary_currency,
        monthly_base: r.monthly_base,
        variable_base: r.variable_base,
        components: r.components,
        totals: r.totals,
      })),
      summary: {
        employee_count: uploadResult.valid_rows,
        total_additions: uploadResult.rows.reduce((a, r) => a + (r.totals?.additions || 0), 0),
        total_deductions: uploadResult.rows.reduce((a, r) => a + (r.totals?.deductions || 0), 0),
        total_net_pay: uploadResult.rows.reduce((a, r) => a + (r.totals?.net_pay || 0), 0),
      },
    };
  }, [uploadResult]);

  const componentColMap = useMemo(() => {
    if (!bulkPreview) return {};
    const m = {};
    bulkPreview.components.forEach((c) => { m[c.id] = c; });
    return m;
  }, [bulkPreview]);

  return (
    <div className="space-y-6" data-testid="bulk-upload-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-indigo-600" />
            Bulk Upload — Excel
          </CardTitle>
          <CardDescription>
            Download the template, fill component values per employee (one row per employee-cycle) and
            upload. Payslips use the values in the sheet exactly as-is — no proration is applied. Missing
            employee fields (Bank, PAN, DOJ etc.) fall back to their <em>Emp_Tab</em> values.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              data-testid="download-template-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Download Template
            </Button>
            <label className="inline-flex" data-testid="bulk-file-label">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="bulk-file-input"
              />
              <span>
                <Button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="bulk-file-upload-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  Upload Excel
                </Button>
              </span>
            </label>
            {fileName && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded text-sm">
                <FileSpreadsheet className="w-4 h-4 text-emerald-600" />
                <span>{fileName}</span>
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="clear-bulk-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {uploadResult && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <StatBlock label="Rows parsed" value={uploadResult.total_rows} tone="slate" />
              <StatBlock label="Valid" value={uploadResult.valid_rows} tone="emerald" />
              <StatBlock label="Errors" value={uploadResult.error_rows} tone={uploadResult.error_rows > 0 ? 'red' : 'slate'} />
            </div>
          )}

          {uploadResult?.errors?.length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4" data-testid="bulk-errors">
              <div className="flex items-center gap-2 text-red-800 font-semibold mb-2">
                <FileWarning className="w-4 h-4" />
                {uploadResult.errors.length} row(s) skipped
              </div>
              <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                {uploadResult.errors.map((err, i) => (
                  <li key={i}>Row {err.row}: {err.reason}</li>
                ))}
              </ul>
            </div>
          )}

          {bulkPreview && (
            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={() => setSavePrompt(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-bulk-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Save {uploadResult.valid_rows} Payslip(s)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {bulkPreview && (
        <PreviewSection
          preview={bulkPreview}
          componentColMap={componentColMap}
          expandedRow={expandedRow}
          setExpandedRow={setExpandedRow}
        />
      )}

      <AlertDialog open={savePrompt} onOpenChange={setSavePrompt}>
        <AlertDialogContent data-testid="save-bulk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save Bulk Pay Distribution?</AlertDialogTitle>
            <AlertDialogDescription>
              This will persist {uploadResult?.valid_rows} payslip(s) and add each one to the employee&apos;s Payslip portal.
              Optionally, email the payslip to each employee (values from the sheet are used as-is).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-700 select-none px-1">
            <input
              type="checkbox"
              checked={sendEmailsOnSave}
              onChange={(e) => setSendEmailsOnSave(e.target.checked)}
              data-testid="bulk-send-emails-checkbox"
              className="w-4 h-4"
            />
            <Mail className="w-4 h-4 text-slate-500" />
            Also email the payslip to each employee
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="save-bulk-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinalizeBulk}
              disabled={savingBulk}
              data-testid="save-bulk-confirm"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingBulk ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {sendEmailsOnSave ? 'Save & Email' : 'Save Only'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatBlock = ({ label, value, tone = 'slate' }) => {
  const map = {
    slate: 'border-slate-200 bg-slate-50 text-slate-800',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    red: 'border-red-200 bg-red-50 text-red-800',
  };
  return (
    <div className={`rounded border ${map[tone] || map.slate} px-4 py-3`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
};

// ============= Delivery Dashboard =====================================

/**
 * DeliveryDashboard — surfaces per-employee payslip email status on the
 * Run Detail card so admins can spot delivery issues without diving into
 * server logs. Uses the ``email_status`` field written by the backend's
 * ``_email_fanout`` (values: 'sent' | 'failed' | 'no_email' | 'pending').
 *
 * Renders a summary strip (Sent / Failed / No email / Pending counts) plus
 * a compact table of the affected employees so the admin knows who exactly
 * to chase.
 */
const DeliveryDashboard = ({ rows, run }) => {
  const summary = useMemo(() => {
    const s = { sent: 0, failed: 0, no_email: 0, pending: 0 };
    rows.forEach((r) => {
      const st = r.email_status || 'pending';
      if (st === 'sent') s.sent += 1;
      else if (st === 'failed') s.failed += 1;
      else if (st === 'no_email') s.no_email += 1;
      else s.pending += 1;
    });
    return s;
  }, [rows]);

  const problems = useMemo(() => rows.filter((r) => (r.email_status || 'pending') !== 'sent'), [rows]);

  // Guard: pre-existing runs (before this feature shipped) have no
  // ``email_status`` at all AND weren't marked with ``send_emails=true``.
  // Nothing useful to show there — collapse the block.
  const anyStatusRecorded = rows.some((r) => r.email_status);
  if (!anyStatusRecorded && !run?.send_emails) return null;

  const totalSent = run?.email_sent_count ?? summary.sent;
  const totalErrors = run?.email_error_count ?? (summary.failed + summary.no_email);
  const total = rows.length;
  const completed = !!run?.email_completed_at;

  return (
    <Card data-testid="delivery-dashboard">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-5 h-5 text-indigo-600" />
          Email Delivery Status
          {!completed && (run?.send_emails || anyStatusRecorded) && summary.pending > 0 && (
            <Badge variant="outline" className="text-[10px] uppercase text-amber-700 border-amber-300 bg-amber-50">
              In progress
            </Badge>
          )}
          {completed && (
            <Badge variant="outline" className="text-[10px] uppercase text-emerald-700 border-emerald-300 bg-emerald-50">
              Completed
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Per-employee outcome for this run. {run?.last_resend_at ? (
            <span>Last resend: <strong>{new Date(run.last_resend_at).toLocaleString('en-IN', { hour12: false })}</strong> by {run.last_resend_by || '—'}.</span>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <DeliveryStat label="Sent" value={summary.sent} tone="emerald" testid="delivery-sent" />
          <DeliveryStat label="Failed" value={summary.failed} tone="red" testid="delivery-failed" />
          <DeliveryStat label="No email" value={summary.no_email} tone="amber" testid="delivery-no-email" />
          <DeliveryStat label="Pending" value={summary.pending} tone="slate" testid="delivery-pending" />
        </div>
        <div className="text-xs text-slate-500">
          {totalSent} / {total} delivered · {totalErrors} error(s) · fanout {completed ? 'completed' : 'in progress'}.
        </div>
        {problems.length > 0 && (
          <div className="border rounded overflow-x-auto">
            <table className="w-full text-sm" data-testid="delivery-problems-table">
              <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Employee</th>
                  <th className="text-left px-3 py-2">Email</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Last Attempt</th>
                  <th className="text-left px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {problems.map((r) => {
                  const st = r.email_status || 'pending';
                  return (
                    <tr key={r.payslip_id || r.employee_id} className="border-t" data-testid={`delivery-row-${r.employee_id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium">{r.name}</div>
                        <div className="text-xs text-slate-500">{r.employee_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{r.email || <em className="text-slate-400">—</em>}</td>
                      <td className="px-3 py-2"><DeliveryBadge status={st} /></td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {r.email_last_attempt_at ? new Date(r.email_last_attempt_at).toLocaleString('en-IN', { hour12: false }) : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-red-600">{r.email_last_error || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DeliveryStat = ({ label, value, tone, testid }) => {
  const map = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
  };
  return (
    <div className={`rounded border ${map[tone]} px-3 py-2`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
};

const DeliveryBadge = ({ status }) => {
  const cfg = {
    sent: { label: 'Sent', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
    no_email: { label: 'No email', className: 'bg-amber-50 text-amber-800 border-amber-200' },
    pending: { label: 'Pending', className: 'bg-slate-50 text-slate-600 border-slate-200' },
  }[status] || { label: status, className: 'bg-slate-50 text-slate-600 border-slate-200' };
  return (
    <Badge variant="outline" className={`text-[10px] uppercase ${cfg.className}`}>
      {cfg.label}
    </Badge>
  );
};

// ============= Run History Section =====================================

/**
 * RunHistorySection — Admin-facing archive of every pay-distribution run
 * (manual + bulk-upload). Renders a scrollable list with per-run stats and
 * two actions per row:
 *   • Excel — downloads the run rebuilt into an XLSX (bulk-upload compatible)
 *   • View  — expands to show the same distribution matrix + expandable
 *             payslip cards that the admin saw at save time
 *
 * All data comes from ``GET /api/payroll/pay-distribution/runs`` (list) and
 * ``GET /api/payroll/pay-distribution/runs/:id`` (per-run detail).
 */
const RunHistorySection = ({ onReopenIntoManual, refreshTick = 0 }) => {
  const token = localStorage.getItem('token');
  const config = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [openRunId, setOpenRunId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  // Re-run confirmation dialog state — one dialog, three intents.
  // ``intent`` ∈ {'reopen', 'duplicate', 'resend'}.
  const [rerunPrompt, setRerunPrompt] = useState({ open: false, intent: null, run: null });
  const [rerunBusy, setRerunBusy] = useState(false);
  const [duplicateEmails, setDuplicateEmails] = useState(true);
  // When true, the Resend action skips employees whose latest attempt is
  // already ``sent`` — targets only failures / no-email / never-tried.
  const [resendOnlyFailed, setResendOnlyFailed] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payroll/pay-distribution/runs?limit=200`, config);
      setRuns(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const openDetail = async (run) => {
    if (openRunId === run.id) { setOpenRunId(null); setDetail(null); return; }
    setOpenRunId(run.id);
    await fetchDetail(run.id);
  };

  const fetchDetail = async (runId) => {
    setDetail(null);
    setDetailLoading(true);
    setExpandedRow(null);
    try {
      const res = await axios.get(`${API}/payroll/pay-distribution/runs/${runId}`, config);
      // Shape the response for the shared PreviewSection.
      const rows = (res.data.payslips || []).map((p) => ({
        user_id: p.employee_id,
        employee_id: p.employee_code,
        name: p.name,
        email: p.email,
        department: '',
        designation: '',
        salary_currency: p.salary_currency || 'INR',
        monthly_base: p.monthly_base,
        variable_base: p.variable_base,
        components: p.components || [],
        totals: p.totals || { additions: 0, deductions: 0, net_pay: 0 },
        // Delivery Dashboard fields — passthrough from the payslip doc.
        email_status: p.email_status || null,
        email_last_attempt_at: p.email_last_attempt_at || null,
        email_last_error: p.email_last_error || null,
        payslip_id: p.id,
      }));
      const componentDict = new Map();
      rows.forEach((r) => (r.components || []).forEach((c) => componentDict.set(c.id || c.name, {
        id: c.id || c.name, name: c.name, component_type: c.component_type,
        is_deduction: c.is_deduction,
      })));
      setDetail({
        run: res.data.run,
        preview: {
          month: res.data.run.month,
          year: res.data.run.year,
          month_label: res.data.run.month_label,
          cycle_from: res.data.run.cycle_from,
          cycle_to: res.data.run.cycle_to,
          days_in_cycle: res.data.run.days_in_cycle || 0,
          days_in_month: res.data.run.days_in_month || 0,
          proration_factor: res.data.run.proration_factor || 1,
          components: Array.from(componentDict.values()),
          rows,
          summary: {
            employee_count: rows.length,
            total_additions: res.data.run.total_additions,
            total_deductions: res.data.run.total_deductions,
            total_net_pay: res.data.run.total_net_pay,
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load run');
      setOpenRunId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadExcel = async (run) => {
    setDownloadingId(run.id);
    try {
      const res = await axios.get(
        `${API}/payroll/pay-distribution/runs/${run.id}/excel`,
        { ...config, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const stamp = `${run.year}-${String(run.month || '').padStart(2, '0')}`;
      a.download = `pay-distribution-${stamp}-${run.id.slice(0, 8)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Excel download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  // ---- Re-run this cycle ------------------------------------------------
  const openRerun = (intent, run) => {
    setDuplicateEmails(true);
    setResendOnlyFailed(true);
    setRerunPrompt({ open: true, intent, run });
  };

  const closeRerun = () => setRerunPrompt({ open: false, intent: null, run: null });

  const runRerunAction = async () => {
    const { intent, run } = rerunPrompt;
    if (!intent || !run) return;
    setRerunBusy(true);
    try {
      if (intent === 'reopen') {
        onReopenIntoManual && onReopenIntoManual(run);
        closeRerun();
        return;
      }
      if (intent === 'duplicate') {
        const res = await axios.post(
          `${API}/payroll/pay-distribution/runs/${run.id}/duplicate`,
          { send_emails: duplicateEmails },
          config,
        );
        toast.success(res.data?.message || 'Run duplicated');
        closeRerun();
        await load();
        return;
      }
      if (intent === 'resend') {
        const res = await axios.post(
          `${API}/payroll/pay-distribution/runs/${run.id}/resend-emails`,
          { only_failed: resendOnlyFailed },
          config,
        );
        toast.success(res.data?.message || 'Re-sending payslip emails');
        closeRerun();
        await load();
        // If the detail card is open for this run, refresh its payslips so
        // the delivery statuses re-render once the fanout finishes.
        if (openRunId === run.id) {
          setTimeout(() => fetchDetail(run.id), 500);
        }
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setRerunBusy(false);
    }
  };

  const componentColMap = useMemo(() => {
    if (!detail) return {};
    const m = {};
    detail.preview.components.forEach((c) => { m[c.id] = c; });
    return m;
  }, [detail]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="run-history-loading">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="run-history-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="w-5 h-5 text-indigo-600" />
            Pay Distribution Run History
          </CardTitle>
          <CardDescription>
            Every manual and bulk-upload run is archived here. Download the run as Excel
            (bulk-upload compatible) or expand to view the payslip matrix again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-runs-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
          {runs.length === 0 ? (
            <div className="py-8 text-center text-slate-500 border border-dashed rounded">
              No saved runs yet. Runs saved via <em>Manual Selection</em> or <em>Bulk Upload</em> will appear here.
            </div>
          ) : (
            <div className="border rounded overflow-x-auto">
              <table className="w-full text-sm" data-testid="run-history-table">
                <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-3 py-2">Generated</th>
                    <th className="text-left px-3 py-2">Cycle</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-right px-3 py-2">Employees</th>
                    <th className="text-right px-3 py-2">Total Additions</th>
                    <th className="text-right px-3 py-2">Total Deductions</th>
                    <th className="text-right px-3 py-2">Net Pay</th>
                    <th className="text-left px-3 py-2">By</th>
                    <th className="text-right px-3 py-2 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => {
                    const isSuperseded = r.status === 'superseded';
                    const isBulk = (r.source || 'manual') !== 'manual' && r.source !== 'duplicate';
                    return (
                      <tr
                        key={r.id}
                        className={`border-t hover:bg-slate-50 ${isSuperseded ? 'opacity-60' : ''} ${openRunId === r.id ? 'bg-indigo-50/40' : ''}`}
                        data-testid={`run-row-${r.id}`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                          {r.generated_at ? new Date(r.generated_at).toLocaleString('en-IN', { hour12: false }) : '—'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="font-medium flex items-center gap-2">
                            {r.month_label}
                            {isSuperseded && (
                              <Badge variant="outline" className="text-[10px] uppercase text-slate-500 border-slate-300" data-testid={`superseded-badge-${r.id}`}>
                                Superseded
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{r.cycle_from} → {r.cycle_to}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {r.source || 'manual'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.employee_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                          ₹{Number(r.total_additions || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-600">
                          ₹{Number(r.total_deductions || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">
                          ₹{Number(r.total_net_pay || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">{r.generated_by}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-right">
                          <div className="inline-flex gap-1.5 flex-wrap justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetail(r)}
                              data-testid={`view-run-${r.id}`}
                            >
                              {openRunId === r.id ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadExcel(r)}
                              disabled={downloadingId === r.id}
                              data-testid={`download-run-${r.id}`}
                              title="Download Excel"
                            >
                              {downloadingId === r.id
                                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                : <Download className="w-3 h-3 mr-1" />}
                              Excel
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRerun('reopen', r)}
                              disabled={isBulk || isSuperseded}
                              title={isBulk ? 'Reopen is available for manual runs. For bulk runs use Duplicate & Send.' : 'Reopen this run into the Manual form'}
                              data-testid={`reopen-run-${r.id}`}
                            >
                              <RotateCw className="w-3 h-3 mr-1" /> Reopen
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRerun('duplicate', r)}
                              disabled={isSuperseded}
                              data-testid={`duplicate-run-${r.id}`}
                              title="Duplicate as a new run and optionally re-email"
                            >
                              <Copy className="w-3 h-3 mr-1" /> Duplicate
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openRerun('resend', r)}
                              disabled={isSuperseded}
                              data-testid={`resend-run-${r.id}`}
                              title="Re-send payslip emails to each employee on this run"
                            >
                              <Send className="w-3 h-3 mr-1" /> Resend
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expanded run detail — rendered OUTSIDE the runs table so wide
          distribution matrices don't push the table beyond the viewport.
          Only one detail is visible at a time (matches openRunId). */}
      {openRunId && (
        <Card data-testid={`run-detail-${openRunId}`}>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                Run Detail
                {detail?.run?.status === 'superseded' && (
                  <Badge variant="outline" className="text-[10px] uppercase text-slate-500 border-slate-300">
                    Superseded
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {detail?.run?.month_label || '…'} · {detail?.run?.cycle_from} → {detail?.run?.cycle_to}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              {(() => {
                const active = runs.find((x) => x.id === openRunId);
                if (!active) return null;
                const superseded = active.status === 'superseded';
                const bulk = (active.source || 'manual') !== 'manual' && active.source !== 'duplicate';
                return (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRerun('reopen', active)}
                      disabled={bulk || superseded}
                      data-testid={`detail-reopen-${active.id}`}
                    >
                      <RotateCw className="w-3 h-3 mr-1" /> Reopen into Manual
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRerun('duplicate', active)}
                      disabled={superseded}
                      data-testid={`detail-duplicate-${active.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Duplicate &amp; Send
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRerun('resend', active)}
                      disabled={superseded}
                      data-testid={`detail-resend-${active.id}`}
                    >
                      <Send className="w-3 h-3 mr-1" /> Resend Emails
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setOpenRunId(null); setDetail(null); }}
                      data-testid="close-run-detail"
                    >
                      <X className="w-3 h-3 mr-1" /> Close
                    </Button>
                  </>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="flex items-center gap-2 text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading run details…
              </div>
            ) : detail && detail.run.id === openRunId ? (
              <div className="min-w-0 space-y-6">
                <DeliveryDashboard rows={detail.preview.rows} run={detail.run} />
                <PreviewSection
                  preview={detail.preview}
                  componentColMap={componentColMap}
                  expandedRow={expandedRow}
                  setExpandedRow={setExpandedRow}
                />
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Re-run confirmation dialog (shared across the 3 intents) */}
      <AlertDialog
        open={rerunPrompt.open}
        onOpenChange={(o) => { if (!o) closeRerun(); }}
      >
        <AlertDialogContent data-testid="rerun-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {rerunPrompt.intent === 'reopen' && `Reopen ${rerunPrompt.run?.month_label || 'this run'}?`}
              {rerunPrompt.intent === 'duplicate' && `Duplicate ${rerunPrompt.run?.month_label || 'this run'}?`}
              {rerunPrompt.intent === 'resend' && `Re-send payslips for ${rerunPrompt.run?.month_label || 'this run'}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {rerunPrompt.intent === 'reopen' && (
                <>Loads all {rerunPrompt.run?.employee_count || 0} employee(s), the same salary components and the same cycle window into the Manual Selection form. Saving there will supersede this run.</>
              )}
              {rerunPrompt.intent === 'duplicate' && (
                <>Creates a fresh run with the same {rerunPrompt.run?.employee_count || 0} payslip(s) (same figures, new run ID). The original run will be marked as superseded.</>
              )}
              {rerunPrompt.intent === 'resend' && (
                <>Re-delivers the payslip PDF to employees on this run. Use the option below to skip recipients who already received it.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {rerunPrompt.intent === 'duplicate' && (
            <label className="flex items-center gap-2 text-sm text-slate-700 select-none px-1">
              <input
                type="checkbox"
                checked={duplicateEmails}
                onChange={(e) => setDuplicateEmails(e.target.checked)}
                data-testid="duplicate-emails-checkbox"
                className="w-4 h-4"
              />
              <Mail className="w-4 h-4 text-slate-500" />
              Also re-send the payslip email to each employee
            </label>
          )}
          {rerunPrompt.intent === 'resend' && (
            <div className="space-y-2 px-1">
              <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
                <input
                  type="radio"
                  name="resend-mode"
                  checked={resendOnlyFailed}
                  onChange={() => setResendOnlyFailed(true)}
                  data-testid="resend-mode-failed"
                  className="mt-1 w-4 h-4"
                />
                <div>
                  <div className="font-medium">Only failed / not sent</div>
                  <div className="text-xs text-slate-500">Skips employees whose latest attempt is marked <em>sent</em>. Safer, avoids duplicate deliveries.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 text-sm text-slate-700 select-none">
                <input
                  type="radio"
                  name="resend-mode"
                  checked={!resendOnlyFailed}
                  onChange={() => setResendOnlyFailed(false)}
                  data-testid="resend-mode-all"
                  className="mt-1 w-4 h-4"
                />
                <div>
                  <div className="font-medium">Everyone on this run</div>
                  <div className="text-xs text-slate-500">Re-delivers the payslip PDF to all {rerunPrompt.run?.employee_count || 0} employee(s), including already-sent recipients.</div>
                </div>
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rerun-cancel" disabled={rerunBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runRerunAction}
              disabled={rerunBusy}
              data-testid="rerun-confirm"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {rerunBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {rerunPrompt.intent === 'reopen' && 'Reopen'}
              {rerunPrompt.intent === 'duplicate' && (duplicateEmails ? 'Duplicate & Send' : 'Duplicate')}
              {rerunPrompt.intent === 'resend' && (resendOnlyFailed ? 'Resend to Failed Only' : 'Resend to Everyone')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

